export type UserRole = 'PATIENT' | 'DOCTOR' | 'ADMIN' | 'SUPPORT' | 'FINANCE' | 'SYSTEM';

export type UserStatus = 'ACTIVE' | 'PENDING' | 'BLOCKED' | 'DELETED';

export interface User {
  id: string;
  email: string;
  name?: string;
  role: UserRole;
  status: UserStatus;
  isPartner?: boolean;
  partnerId?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface Session {
  id: string;
  userId: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  userAgent?: string;
  ipAddress?: string;
}

export interface SessionsResponse {
  sessions: Session[];
  activeSessionId: string | null;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string, loginResponse?: LoginResponse) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
  requiresTwoFactor?: boolean;
  pendingSessionId?: string;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  role?: UserRole;
}

export interface RegisterResponse {
  user: User;
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  message: string;
}

export interface RequestEmailVerificationRequest {
  email: string;
}

export interface RequestEmailVerificationResponse {
  message: string;
}

export interface Verify2FARequest {
  pendingSessionId: string;
  userId: string;
  code: string;
  isBackupCode?: boolean;
}

export interface Verify2FAResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'ACCOUNT_BLOCKED'
  | 'RATE_LIMIT'
  | 'MFA_REQUIRED'
  | 'INVALID_TOKEN'
  | 'TOKEN_EXPIRED'
  | 'EMAIL_NOT_VERIFIED'
  | 'INVALID_2FA_CODE'
  | 'INVALID_BACKUP_CODE'
  | 'USER_NOT_FOUND'
  | 'EMAIL_ALREADY_EXISTS'
  | 'WEAK_PASSWORD'
  | 'VALIDATION_ERROR'
  | 'AUTH_INVALID_VERIFICATION_TOKEN'
  | 'UNKNOWN_ERROR';

export interface AuthError {
  code: AuthErrorCode;
  message: string;
  retryAfter?: number;
}

export class AuthApiError extends Error {
  constructor(
    public code: AuthErrorCode,
    message: string,
    public retryAfter?: number
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}
